import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import { formatText } from "@ui/format";
import { logError } from "@utils/sentry-error";
import type { ReactNode } from "react";
import React from "react";
import * as Step from "./Steps";
import { calculate } from "./domain";
import type {
	FormFields,
	LifeInsuranceLeadCalcRequest,
	LifeInsuranceLeadCalcResponse,
	RiskInsuranceResponse,
} from "./types";

interface State<TFormFields extends FormFields = FormFields> extends AbstractLeadFormState<TFormFields> {
	calculatorResponse: RiskInsuranceResponse;
	showDetectiveAlert: boolean;
}

/**
 * @Shortcode
 *
 * @title "Kockázati kalkulátor"
 * @shortcode "risk_insurance_small"
 * @slug "kockazati-biztositas"
 * @type "calculator"
 */
export class RiskInsuranceSmallCalculator extends AbstractLeadForm<
	LifeInsuranceLeadCalcRequest,
	LifeInsuranceLeadCalcResponse,
	FormFields,
	AbstractLeadFormProps<LifeInsuranceLeadCalcRequest, LifeInsuranceLeadCalcResponse>,
	State
> {
	// EventParams
	protected readonly productTypes: string = "LIFE_INSURANCE";
	protected readonly componentType: string = "small-calculator";

	public static defaultProps = {
		title: "Kérj ingyenes segítséget szakértőnktől!",
		outsideTitle: "Kockázati életbiztosítás",
		outsideTitleLink: "/biztositas/eletbiztositas/",
		bottomLogo: true,
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			calculatorResponse: undefined,
			showDetectiveAlert: false,
		};
	}

	public componentDidMount(): void {
		super.componentDidMount();

		// NOTE: This is a workaround to work well with prerender
		this.setState({ showDetectiveAlert: !window.location.search.includes("disableDetective") });
	}

	protected renderSteps(): ReactNode {
		const {
			values: { email, durationYears },
			submitState,
			calculatorResponse,
			showDetectiveAlert: enabledDetective,
		} = this.state;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="step-2" showDetectiveAlert={enabledDetective} />
				<Step.Step2 stepName="step-2" defaultTarget="result" submitState={submitState} canStepBack />
				<Step.Result
					stepName="result"
					defaultTarget="lead-form"
					durationYears={durationYears}
					sumPrice={calculatorResponse?.sumPrice}
					canStepBack
				/>
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-page"
					button="Eredmény küldése"
					submitState={submitState}
				/>
				<Step.SuccessPage stepName="success-page" subscriptionTypes={["RETAIL_NEWSLETTER"]} userEmail={email} />
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "result":
				this.onResult(target);
				break;
			case "success-page":
				this.onSuccessPage();
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	private onResult(target: string): void {
		const { values } = this.state;

		this.setState({ submitState: "loading" });

		this.promisePool
			.add(calculate(values))
			.then((calculatorResponse: RiskInsuranceResponse) => {
				this.setState(
					{
						calculatorResponse,
						submitState: undefined,
					},
					() => {
						super.onStep(target, true);
						this.sendViewItemListEvent();
					},
				);

				this.sendCalculationEvent();
			})
			.catch((error: Error) => {
				this.setState({ submitState: "error" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
	}

	private onSuccessPage(): void {
		this.setState({
			submitState: "loading",
		});
		this.sendLead()
			.then((_response) => {
				super.onStep("success-page", false);
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			});
	}

	protected getLeadData(): LeadRequest<LifeInsuranceLeadCalcRequest, LifeInsuranceLeadCalcResponse> {
		const {
			calculatorResponse: {
				anyDisabilityInsuranceAmount,
				fatalAccidentInsuranceAmount,
				dreadDiseaseInsuranceAmount,
				accidentDisabilityInsuranceAmount,
				anyDisabilityInsurancePrice,
				fatalAccidentInsurancePrice,
				accidentDisabilityInsurancePrice,
				dreadDiseaseInsurancePrice,
				riskPrice,
			},
			values: { riskInsuranceAmount, mainInsuranceGoal, question, birthYear, durationYears },
		} = this.state;
		const data = super.getLeadData();

		data.request.question = question;
		data.request.birthYear = birthYear;
		data.request.preferredProduct =
			mainInsuranceGoal === "RISK_INSURANCE" ? "Kockázati életbiztosítás" : "Hitelfedezeti védelem";

		data.request.isCreditInsurance = mainInsuranceGoal === "CREDIT_INSURANCE";
		data.request.isRiskInsurance = mainInsuranceGoal === "RISK_INSURANCE";
		data.request.isDisabilityInsurance = anyDisabilityInsuranceAmount > 0;
		data.request.isFatalAccident = fatalAccidentInsuranceAmount > 0;
		data.request.isAccidentDisability = accidentDisabilityInsuranceAmount > 0;
		data.request.isDreadDisease = dreadDiseaseInsuranceAmount > 0;
		data.request.insuranceDurationYears = durationYears;

		if (data.request.isCreditInsurance) {
			data.response.insurancePriceCredit = Math.round(riskPrice * 0.85);
			data.response.insuranceAmountCredit = Math.round(riskInsuranceAmount);
		}

		if (data.request.isRiskInsurance) {
			data.response.insurancePriceRisk = Math.round(riskPrice * 0.85);
			data.response.insuranceAmountRisk = Math.round(riskInsuranceAmount);
		}
		data.response.insuranceAmountDisability = Math.round(anyDisabilityInsuranceAmount);
		data.response.insuranceAmountFatalAccident = Math.round(fatalAccidentInsuranceAmount);
		data.response.insuranceAmountAccidentDisability = Math.round(accidentDisabilityInsuranceAmount);
		data.response.insuranceAmountDreadDisease = Math.round(dreadDiseaseInsuranceAmount);
		data.response.insurancePriceDisability = Math.round(anyDisabilityInsurancePrice * 0.85);
		data.response.insurancePriceFatalAccident = Math.round(fatalAccidentInsurancePrice * 0.85);
		data.response.insurancePriceAccidentDisability = Math.round(accidentDisabilityInsurancePrice * 0.85);
		data.response.insurancePriceDreadDisease = Math.round(dreadDiseaseInsurancePrice * 0.85);

		return data;
	}

	protected getAutoresponderName(): string {
		return "autoresponder-risk-insurance";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { firstName, lastName, durationYears, riskInsuranceAmount, mainInsuranceGoal },
			calculatorResponse: {
				anyDisabilityInsuranceAmount,
				fatalAccidentInsuranceAmount,
				dreadDiseaseInsuranceAmount,
				accidentDisabilityInsuranceAmount,
				dreadDiseaseInsurancePrice,
				accidentDisabilityInsurancePrice,
				anyDisabilityInsurancePrice,
				fatalAccidentInsurancePrice,
				sumPrice,
				riskPrice,
			},
		} = this.state;

		return {
			lastName,
			firstName,

			durationYears,
			riskInsuranceAmount: formatText(riskInsuranceAmount, "ft"),
			creditInsurance: mainInsuranceGoal === "CREDIT_INSURANCE",
			riskInsurance: mainInsuranceGoal === "RISK_INSURANCE",

			anyDisabilityInsuranceAmount: anyDisabilityInsuranceAmount
				? formatText(anyDisabilityInsuranceAmount, "ft")
				: undefined,
			fatalAccidentInsuranceAmount: fatalAccidentInsuranceAmount
				? formatText(fatalAccidentInsuranceAmount, "ft")
				: undefined,
			dreadDiseaseInsuranceAmount: dreadDiseaseInsuranceAmount
				? formatText(dreadDiseaseInsuranceAmount, "ft")
				: undefined,
			accidentDisabilityInsuranceAmount: accidentDisabilityInsuranceAmount
				? formatText(accidentDisabilityInsuranceAmount, "ft")
				: undefined,

			dreadDiseaseInsurancePrice: formatText(dreadDiseaseInsurancePrice * 0.85, "ft"),
			accidentDisabilityInsurancePrice: formatText(accidentDisabilityInsurancePrice * 0.85, "ft"),
			anyDisabilityInsurancePrice: formatText(anyDisabilityInsurancePrice * 0.85, "ft"),
			fatalAccidentInsurancePrice: formatText(fatalAccidentInsurancePrice * 0.85, "ft"),

			sumPrice: formatText(sumPrice * 0.85, "ft"),
			riskPrice: formatText(riskPrice * 0.85, "ft"),
		};
	}

	protected getProductType(): ProductType {
		return "LIFE_INSURANCE";
	}

	protected getClassName(): string {
		return `${super.getClassName()} risk-insurance`;
	}

	private sendViewItemListEvent(): void {
		const {
			calculatorResponse,
			values: { durationYears },
		} = this.state;

		this.sendEvent("view_item_list", {
			item_list_id: this.calculationId,
			item_list_name: "normal",
			items: [
				{
					item_id: "",
					item_brand: "",
					item_name: "",
					affiliation: "product",
					index: 1,
					coupon: "none",
					currency: "HUF",
					discount: 0,
					item_category: "",
					item_category2: "",
					item_category3: "",
					item_category4: "",
					item_category5: "",
					item_variant: ``,
					price: Math.floor(calculatorResponse?.sumPrice),
					quantity: durationYears,
				},
			],
		});
	}

	protected getComponentName(): string {
		return "RiskInsuranceSmallCalculator";
	}
}

export default RiskInsuranceSmallCalculator;
